import { hasAreaProtectionAndAddress } from '../../config';

export type AreaProtectionLevel = 'danger' | 'warning' | 'normal';

export interface AreaProtectionItem {
  id: string;
  name: string;
  type: string;
  walkingDistance: string;
  status: string;
  protectionEndDate: string;
}

export interface AreaProtectionResult {
  level: AreaProtectionLevel;
  title: string;
  summary: string;
  items: AreaProtectionItem[];
  allowApply: boolean;
}

export interface CollisionCheckItem {
  name?: string;
  distance?: number;
  type?: string;
  status?: string;
  protectionExpiry?: string;
}

export interface CollisionCheckGroup {
  range?: string;
  items?: CollisionCheckItem[];
}

export interface ChanceApplicationInfo {
  chanceName: string;
  city: string;
  address: string;
  franchiseeName: string;
}

const parseTextValue = (value: any) => {
  if (typeof value !== 'string') return value || {};
  if (!/^[{[]/.test(value.trim())) return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.log('解析区域保护申请机会点信息失败', error);
    return value;
  }
};

const getComponentDisplayValue = (component: any) => {
  const value = parseTextValue(component?.textValue);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map((item) => item?.name || item?.label || item).filter(Boolean).join('、');
  return value?.name || value?.label || value?.value || '';
};

/** 从当前动态详情字段读取特殊申请抽屉中的机会点基本信息。 */
export const getChanceApplicationInfo = (allComponents: any[] = []): ChanceApplicationInfo => {
  const chanceNameComponent = allComponents.find((item) => item?.identification === 'basicChancePointName');
  const areaComponent = allComponents.find((item) => item?.identification === 'area');
  const addressComponent = allComponents.find((item) => item?.identification === 'basicAddress');
  const franchiseeComponent = allComponents.find((item) => /franchisee/i.test(item?.identification || '') || String(item?.name || '').includes('加盟商'));
  const areaValue = parseTextValue(areaComponent?.textValue);
  const addressValue = parseTextValue(addressComponent?.textValue);
  const cityParts = [areaValue?.provinceName, areaValue?.provinceName === areaValue?.cityName ? '' : areaValue?.cityName, areaValue?.districtName].filter(Boolean);

  return {
    chanceName: getComponentDisplayValue(chanceNameComponent) || '-',
    city: cityParts.length ? cityParts.join('/') : '-',
    address: addressValue?.address || addressValue?.poiName || '-',
    franchiseeName: getComponentDisplayValue(franchiseeComponent) || '-',
  };
};

/** 审批接口未提供前，使用前端申请状态判断是否已发起特殊申请。 */
export const hasInitiatedSpecialApplication = (approvalStatus?: string) => typeof approvalStatus === 'string' && approvalStatus !== 'pending';

/** 保持接口分组结构；红色撞店时同时持久化 200m 和 200-500m 的完整门店列表。 */
export const trimCollisionGroups = (groups: CollisionCheckGroup[] = []): CollisionCheckGroup[] => {
  const hasDanger = hasCollisionWithin200m(groups);
  return groups.map((group) => ({
    ...group,
    items: group.range === '200m内' || (hasDanger && group.range === '200m-500m') ? (group.items || []) : [],
  }));
};

/** 仅在确实撞店并发起申请时持久化防撞店结果。 */
export const shouldPersistSpecialApplication = (approvalStatus: string | undefined, groups: CollisionCheckGroup[] = []) => {
  const dangerGroup = groups.find((group) => group.range === '200m内');
  const dangerItems: CollisionCheckItem[] = dangerGroup?.items || [];
  return hasInitiatedSpecialApplication(approvalStatus) && dangerItems.length > 0;
};

/** 是否存在步行 200m 内的防撞店。 */
export const hasCollisionWithin200m = (groups: CollisionCheckGroup[] = []) => {
  const dangerGroup = groups.find((group) => group.range === '200m内');
  const dangerItems = dangerGroup?.items;
  return Array.isArray(dangerItems) && dangerItems.length > 0;
};

/** 优先使用详细地址本次变更的坐标，避免等待地址组件异步回写 textValue。 */
export const getCollisionCoordinates = (addressValue: any, latestAddressValue?: any): [number, number] | null => {
  const normalizeCoordinates = (value: any) => {
    const longitude = value?.lng ?? value?.longitude;
    const latitude = value?.lat ?? value?.latitude;
    if (!Number.isFinite(Number(longitude)) || !Number.isFinite(Number(latitude))) return null;
    return [Number(longitude), Number(latitude)] as [number, number];
  };

  return normalizeCoordinates(latestAddressValue) || normalizeCoordinates(addressValue);
};

/** 保存机会点时，依据区域保护控件和特殊审批状态计算是否允许拓店任务关联。 */
export const getTaskAssociable = (propertyValues: any[] = [], antiCollisionApprovalStatus?: number, isSjtb = false) => {
  // 防撞店规则仅适用于三津汤包租户，其他租户始终允许关联拓店任务。
  if (!isSjtb) return 1;

  if (!hasAreaProtectionAndAddress(propertyValues)) return 1;

  // 防撞店特殊审批通过后，产品要求机会点始终可关联拓店任务。
  if (antiCollisionApprovalStatus === 1) return 1;

  const areaProtectionValue = propertyValues.find((item: any) => item?.controlType === 58);
  const value = parseTextValue(areaProtectionValue?.textValue);
  return hasCollisionWithin200m(value?.groups) ? 0 : 1;
};

/** 根据防撞结果与特殊申请状态，计算机会点详情顶部的互斥操作。 */
export const getAntiCollisionOperationState = (hasCollisionWithin200m: boolean | undefined, approvalStatus?: number) => {
  if (hasCollisionWithin200m === undefined) {
    return {
      hideSubmitApproval: true,
      showSpecialApplication: false,
    };
  }

  // 特殊申请审核中时，机会点不能再次提交审批。
  if (approvalStatus === 0) {
    return {
      hideSubmitApproval: true,
      showSpecialApplication: false,
    };
  }

  if (!hasCollisionWithin200m || approvalStatus === 1) {
    return {
      hideSubmitApproval: false,
      showSpecialApplication: false,
    };
  }

  return {
    hideSubmitApproval: true,
    showSpecialApplication: true,
  };
};

const formatDistance = (distance?: number) => {
  if (typeof distance !== 'number') return '-';
  if (distance < 1000) return `${distance}m`;
  return `${Number((distance / 1000).toFixed(1))}km`;
};

const adaptItems = (items: CollisionCheckItem[] = []): AreaProtectionItem[] => items.map((item, index) => ({
  id: `${item.type || 'collision'}-${item.name || index}-${item.distance || 0}`,
  name: item.name || '-',
  type: item.type || '-',
  walkingDistance: formatDistance(item.distance),
  status: item.status || '-',
  protectionEndDate: item.protectionExpiry || '-',
}));

const findItemsByRange = (groups: CollisionCheckGroup[], range: string) => {
  const group = groups.find((item) => item.range === range);
  return adaptItems(group?.items);
};

/** 将防撞店接口的距离分组转换为区域保护组件展示数据。 */
export const adaptCollisionCheckResult = (groups: CollisionCheckGroup[] = []): AreaProtectionResult => {
  const dangerItems = findItemsByRange(groups, '200m内');
  const warningItems = findItemsByRange(groups, '200m-500m');
  if (dangerItems.length) {
    return {
      level: 'danger',
      title: '撞店预警',
      summary: '步行 200m 内存在本品牌门店，判定撞店，需走特殊申请方可继续。',
      // 红色预警仍由 200m 内撞店触发，同时完整展示 200-500m 内门店。
      items: [...dangerItems, ...warningItems],
      allowApply: true,
    };
  }

  if (warningItems.length) {
    return {
      level: 'warning',
      title: '距离提醒',
      summary: '步行 200-500m 内存在本品牌门店，仅提醒，可继续操作。',
      items: warningItems,
      allowApply: false,
    };
  }

  const normalItems = findItemsByRange(groups, '500m-30km');
  return {
    level: 'normal',
    title: '未撞店',
    summary: normalItems.length ? `步行 500m 内无本品牌门店，500m-30km有${normalItems.length}家门店。` : '周边 30km 无本品牌门店。',
    // 正常态仅露出最近门店，统计文案仍保留接口返回的完整门店数量。
    items: normalItems.slice(0, 1),
    allowApply: false,
  };
};
